// import { parse, HTMLElement } from 'node-html-parser'
import { useImageResizer } from '~/utils/imageResizer'
import { processUgcContent } from '~/utils/ugcLinks'
import { isUserImage, placeholderSrc } from '~/utils/images'
import {
  IMAGE_LAZYLOAD_CLASS,
  IMAGE_LAZYLOAD_LQIP_CLASS,
  IMAGE_GALLERY_CLASS,
  // FIGURE_DATA_ATTRIBUTE,
  USER_IMAGE_CLASS,
  USER_IMAGE_FULL_CLASS
} from '~/configs/images'
// import { calculateAspectRatioFit } from '~/utils/math'
// import { CONTENT_IMAGES_MAX_HEIGHT, MAIN_CONTENT_INNER_WIDTH } from '~/configs/elements'
import { convertAttributesToString, parseAttributesFromString } from '~/modules/content/images'

const contentManagerDefaultProps = {
  imageAdditionalAttrs: {},
  imageAdditionalClasses: [],
  imageSizes: [],
  isLazy: false,
  isGallery: false
} as ContentManagerImageProps

class ContentManager {
  content: string

  resizer: ImageResizerInterface

  withImages: boolean

  constructor ({ content, resizer, withImages = true }: {
    content: string
    resizer?: ImageResizerInterface
    withImages?: boolean
  }) {
    this.content = content
    this.resizer = resizer || useImageResizer()
    this.withImages = withImages

    this.prepareContent()
  }

  getHtml (params?: ContentManagerImageProps): string {
    if (!this.content) return ''

    if (this.content.includes('<img')) {
      this.prepareImagesNew(params)
    }

    return this.content
  }

  static prepareEditorContent (content: string | null) {
    if (!content) return ''

    return content
      .replace(/<\/?(pre|span)[^>]*>/gi, '')
      .replace(/(?:color=")(.*)(?=")/gi, '')
      .replace(/(<figure>)?<img [^>]*src="[^"]*"[^>]*>(<\/figure>)?/gm, (match) => {
        if (match.includes('<figure')) {
          return match
        }

        return `<figure>${match}</figure>`
      })
  }

  private prepareImagesNew (params?: ContentManagerImageProps) {
    const imageRegex = /<img [^>]*src="[^"]*"[^>]*>/gm

    this.content = this.content.replace(imageRegex, (imageStr) => {
      if (!this.withImages) {
        return ''
      }

      const imageSourceAttributes = parseAttributesFromString(imageStr)

      if (imageSourceAttributes.class) {
        return imageStr
      }

      let src = imageSourceAttributes.src

      if (!src) {
        return ''
      }

      if (process.env.IS_PROD === 'true') {
        src = src.replace('http:', 'https:')
      }

      if (isUserImage(src)) {
        const {
          imageAdditionalAttrs = {},
          imageAdditionalClasses = [],
          imageSizes = [],
          isLazy = false,
          isGallery = false
        } = params || contentManagerDefaultProps
        const imageClasses = [
          ...imageAdditionalClasses,
          USER_IMAGE_CLASS,
          ...imageSourceAttributes.class ? [imageSourceAttributes.class] : [],
          ...isGallery ? [IMAGE_GALLERY_CLASS] : [],
          ...isLazy ? [IMAGE_LAZYLOAD_CLASS, IMAGE_LAZYLOAD_LQIP_CLASS] : []
        ]
        const imageLazyAttributes = isLazy ? {
          'data-src': src,
          ...imageSizes.length
            ? { 'data-srcset': this.resizer.getSrcSetBySizes(src, imageSizes) }
            : null
        } : null
        const width = imageSourceAttributes['data-width']
        const height = imageSourceAttributes['data-height']

        if (width && Number(width) >= 750) {
          imageClasses.push(USER_IMAGE_FULL_CLASS)
        }

        const alt = imageSourceAttributes.alt || imageAdditionalAttrs.alt
        const imageAttributes = convertAttributesToString({
          src: isLazy ? placeholderSrc({ width, height }) : src,
          ...imageAdditionalAttrs,
          ...imageLazyAttributes,
          class: imageClasses.join(' '),
          ...alt ? { alt } : null
        })

        return `<img ${imageAttributes}>`
      }

      return `<img src="${src}">`
    })
  }

  private prepareContent () {
    if (this.content.includes('atob')) {
      this.content = this.content.replace(/atob[^)]*\)/gim, '')
    }

    if (this.content.includes('srcdoc')) {
      this.content = this.content.replace(/(srcdoc)=(\\)?"[^"]*"/gim, '')
    }

    if (this.content.includes('script')) {
      this.content = this.content.replace(/<script[^>]*>.*<\/script>/gim, '')
    }

    this.content = this.content
      .replace(/<p><br(\s)?(\/)?><\/p>/gim, '')
      .replace(/(itemprop|itemscope|itemtype)="[^"]*"/gim, '')
      .replace(/redirect\.php/gm, 'redirect')

    // Обработка UGC ссылок: добавляем атрибуты безопасности, разворачиваем legacy redirect
    this.content = processUgcContent(this.content)
  }
}

export default ContentManager
